/**
 * The viewer's Text tab: a document's extracted text, rendered as React elements.
 * Document content is never inserted as HTML (no dangerouslySetInnerHTML): plain
 * text keeps its line breaks, and Google Docs (exported as markdown) get a small
 * markdown subset — headings, bullet / numbered lists, **bold** and http(s) links.
 * Anything else, including raw HTML tags in the text, shows as literal text.
 */
import React from 'react';
import { DWA } from '../theme/dwaTokens';

const MD_ESCAPE = /\\([\\`*_{}[\]()#+\-.!>|~])/g;

function unescapeMd(s: string): string {
  return s.replace(MD_ESCAPE, '$1');
}

const INLINE = /(\*\*[^*]+\*\*|!?\[[^\]]*\]\([^)\s]+\))/g;

/** **bold**, [text](http…) links; images become their alt text. */
function renderInline(text: string, keyPrefix = 'i'): React.ReactNode[] {
  const out: React.ReactNode[] = [];
  let last = 0;
  let n = 0;
  for (const m of text.matchAll(INLINE)) {
    const idx = m.index ?? 0;
    if (idx > last) out.push(unescapeMd(text.slice(last, idx)));
    const token = m[0];
    if (token.startsWith('**')) {
      out.push(<strong key={`${keyPrefix}-${n++}`}>{unescapeMd(token.slice(2, -2))}</strong>);
    } else {
      const link = /^(!?)\[([^\]]*)\]\(([^)\s]+)\)$/.exec(token);
      const [, bang, label, href] = link ?? ['', '', token, ''];
      if (!bang && /^https?:\/\//i.test(href)) {
        out.push(
          <a key={`${keyPrefix}-${n++}`} href={href} target="_blank" rel="noopener noreferrer" style={{ color: '#1d4ed8' }}>
            {unescapeMd(label || href)}
          </a>,
        );
      } else {
        out.push(unescapeMd(label));
      }
    }
    last = idx + token.length;
  }
  if (last < text.length) out.push(unescapeMd(text.slice(last)));
  return out;
}

const HEADING_SIZES = ['1.25rem', '1.1rem', '1rem', '0.95rem', '0.9rem', '0.9rem'];

export const MarkdownText: React.FC<{ text: string }> = ({ text }) => {
  const blocks: React.ReactNode[] = [];
  let list: { ordered: boolean; items: string[] } | null = null;
  let para: string[] = [];
  const flushPara = () => {
    if (para.length) {
      blocks.push(
        <p key={`p${blocks.length}`} style={{ margin: '0 0 0.7rem' }}>
          {para.map((line, i) => (
            <React.Fragment key={i}>
              {i > 0 && <br />}
              {renderInline(line, `p${blocks.length}-${i}`)}
            </React.Fragment>
          ))}
        </p>,
      );
      para = [];
    }
  };
  const flushList = () => {
    if (list) {
      const Tag = list.ordered ? 'ol' : 'ul';
      blocks.push(
        <Tag key={`l${blocks.length}`} style={{ margin: '0 0 0.7rem', paddingLeft: '1.4rem' }}>
          {list.items.map((item, i) => (
            <li key={i}>{renderInline(item, `l${blocks.length}-${i}`)}</li>
          ))}
        </Tag>,
      );
      list = null;
    }
  };
  for (const raw of text.split(/\r?\n/)) {
    const line = raw.trimEnd();
    const heading = /^(#{1,6})\s+(.*)$/.exec(line);
    const bullet = /^\s*[-*+]\s+(.*)$/.exec(line);
    const numbered = /^\s*\d+[.)]\s+(.*)$/.exec(line);
    if (!line.trim()) {
      flushPara();
      flushList();
    } else if (heading) {
      flushPara();
      flushList();
      const level = heading[1].length;
      blocks.push(
        <div key={`h${blocks.length}`} role="heading" aria-level={Math.min(level + 2, 6)} style={{ fontWeight: 700, fontSize: HEADING_SIZES[level - 1], margin: '0.9rem 0 0.45rem' }}>
          {renderInline(heading[2], `h${blocks.length}`)}
        </div>,
      );
    } else if (bullet || numbered) {
      flushPara();
      const ordered = Boolean(numbered);
      if (list && list.ordered !== ordered) flushList();
      if (!list) list = { ordered, items: [] };
      list.items.push((bullet ?? numbered)![1]);
    } else {
      flushList();
      para.push(line);
    }
  }
  flushPara();
  flushList();
  return <div data-testid="kb-text-markdown">{blocks}</div>;
};

export const KbTextView: React.FC<{ text: string; format: 'markdown' | 'text' }> = ({ text, format }) => (
  <div style={{ fontSize: '0.9rem', lineHeight: 1.6, color: DWA.ink, overflowWrap: 'anywhere' }}>
    {format === 'markdown' ? (
      <MarkdownText text={text} />
    ) : (
      <pre data-testid="kb-text-plain" style={{ whiteSpace: 'pre-wrap', fontFamily: DWA.font, margin: 0 }}>
        {text}
      </pre>
    )}
  </div>
);
